//  libs
import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import { catchError, retry, timeout } from 'rxjs/operators'
import { Temporal } from '@js-temporal/polyfill';
import { CONNECTION_TYPES, ENVIRONMENT, WeekDay } from 'src/app/_constants/app.constants';
import blob from '../../../../../api/_libs/mock/blob.json'

// app
import { ERROR_KEYS, ChatEvents, QuantumMetricEvents, HELP_OPTION_CODES } from 'src/app/_constants/app.constants';
import { UserDetails, CustomFields, User } from 'src/app/_models/user';
import { AnalyticsService } from '../analytics/analytics.service';
import { LoggerService } from '../../ui/logger/logger.service';
import { DepartmentRoutingService } from 'src/app/_services/api/department-routing/department-routing.service';
import { ServiceModule } from 'src/app/_services/service.module';

import { HttpClient } from '@angular/common/http';
import { SESSION_STORAGE_SERVICE } from '../../session/session.service';
import { StorageService } from 'ngx-webstorage-service';

// augment the global window namespace
declare global {
  interface Window { _uww: any; webex: any; webexIO: any; webexRuntimeSessioMode: boolean; webexHold: any; }
}

declare let webexIO: any; // Declare webexIO as any

@Injectable({
  providedIn: ServiceModule,
})
export class WebexService {
  _uww: any;
  webexIO: any;
  webexReady: Subject<boolean> = new Subject<boolean>();
  chatStartEvent: Subject<string> = new Subject<string>();
  agentJoinedEvent: Subject<string> = new Subject<string>();
  chatEndEvent: Subject<string> = new Subject<string>();
  feedbackEndEvent: Subject<boolean> = new Subject<boolean>();
  chatTimeOutEvent: Subject<boolean> = new Subject<boolean>();
  conversationHasEnded: boolean = false;
  initialized = false;
  connectionType = '';
  visitor_token = ''
  callId = '';

  agents = {
    noAgents: false,
    available: true,
    withinWorkingHours: true,
    noAgentsError: ERROR_KEYS.NO_AGENTS
  };

  private _userDetails: UserDetails;
  w: Partial<Window>;
  private webexUrl = "https://custservice.united.upstreamworkssoftware.com";

  get userDetails() {
    return this._userDetails;
  }

  webexServiceStreamer = new Subject();
  isNoAgents = false;
  chatId = '';

  constructor(
    private logger: LoggerService,
    private analyticsService: AnalyticsService,
    private departmentRoutingService: DepartmentRoutingService,
    private http: HttpClient,
    @Inject(DOCUMENT) private readonly d: any,
    @Inject(SESSION_STORAGE_SERVICE) private storage: StorageService,
  ) {

    this.w = this.d.defaultView;

    this.w.webexHold = []

    // set userDetails
    this._userDetails = {
      email: '',
      fields: {
        'region': '',
        'routing-code': '',
        'airport-code': '',
        'gate-number': '',
        'asset-id': '',
        'reason': '',
        'flight_status': '',
        'stationId': '',
        'flight_no': '',
        'pnr_no': '',
        'departure_date': '',
        'origin_airport': '',
        'destination-airport': '',
        'sms_consent': '',
        'priority_score': '',
        'key_attributes': '',
        'routed-department-id': '',
        'ua_source': '',
        'is_checked_in': '',
        'language': '',
        'auto-message': '',
        'referrer': '',
        'mp-number': '',
        'currentLocation': '',
        'flight-connection-time': '',
        'isSameDayTrip': false,
        'ssr': '',
        'mpStatus': '',
        'callId': '',
        'sessionBandwidth':'',
        'transferEligible':'',
        'chatOnly':''
      },
      meta: '_by:app',
      name: '',
      phone: ''
    };

    // watch variable
    this.webexReady.subscribe(value => this.initialized = value);
  }

  /**
   *
   * Trigger loading of webex
   *
   */
  async loadWebex() {
    return new Promise((resolve, reject) => {
      this.webexUrl = ENVIRONMENT.PROPERTIES.WEBEX_URL ? ENVIRONMENT.PROPERTIES.WEBEX_URL : this.webexUrl;
      this.logger.debug('loadWebex: ', this.webexUrl);

      window["_uww"] = {
        queue: [],
        on: (...args) => { (window["_uww"].onQueue = window["_uww"].onQueue || []).push(args) },
        once: (...args) => { (window["_uww"].onceQueue = window["_uww"].onceQueue || []).push(args) },
        start: (...args) => window["_uww"].queue.push(args),
        customerService: encodeURI(this.webexUrl)
      }
      let e
      e = this.d.createElement("script")
      e.src = `${encodeURI(this.webexUrl)}/extensions/agentnow/widget/platform.js?nc=${Math.round(Date.now() / (60 * 60 * 1000))}`
      this.d.body.appendChild(e)
      e = this.d.createElement("script")
      e.defer = 1
      e.src = `${encodeURI(this.webexUrl)}/extensions/agentnow/widget/widget.js?nc=${Math.round(Date.now() / (60 * 60 * 1000))}`
      e.onload = resolve
      e.onerror = reject
      e.async = true
      e.type = 'module'
      this.d.body.appendChild(e)

    });
  }

  /** 
   * override push method as per UAVA-72
   * to delay execution of webex
   */
  push = (config: any) => this.w.webexHold.push(config);

  /**
   * Check existing session before start session
   * @param params {userData} from sessionService
   */
   async prechat(params: { userData: User }, config?: {}) {
           await this.initialize(config).catch((e) => {
            this.reloadWindow()
           })

  }

  /**
   *
   * Initialize webex widget and events
   *
   */
  initialize(config?: {}) {

    this.logger.debug('WebexService: initialize: ', this.w.webex, this.w.webexHold);

    const self = this;
    const promise = new Promise<void>((resolve, reject) => {

      if (self.initialized) {
        this.logger.debug('WebexService: initialize -- Webex already Initialized, calling the initiate()');
        self.initiate(self.connectionType, config);
        return resolve();

      }
      try {
        // set up webex object
        self.w.webex = self.w.webex || [];

        // delay execution of webex
        self.w.webexHold = [];

        self.w.webex.push = this.push;

        this.loadWebex().then(async () => {
          setTimeout(() => self.initiate(self.connectionType, config), 1000);
          resolve();
        }).catch(error => {
          this.logger.error('WebexService: initialize(): Error loading webex sdk.', error)
          self.analyticsService.sendEvent({
            eventId: QuantumMetricEvents.SCRIPT_ERROR,
            isConversion: 0,
            value: JSON.stringify(error)
          })
          throw error
        });
      } catch (error) {
        this.logger.error('WebexService: initialize(): Error initializing webex sdk.', error)
        reject(error)
      }

    });

    return promise;
  }

  /**
   *
   * Push user location details to webex
   *
   */
  setLocationData(locationData) {
    // set up user details
    this.userDetails.fields = locationData;
    this.logger.debug('WebexService: Setting user location details: ', this.userDetails);
  }

  /**
   *
   * Push user personal details to webex
   *
   */
  setUserData(userData) {
    // set up user details
    this.userDetails.name = `${String(userData.firstName).toUpperCase()} ${String(userData.lastName).toUpperCase()}`;
    this.userDetails.phone = `${userData.phone.code} ${userData.phone.number}`;

    this.logger.debug('WebexService: Setting user details: ', this.userDetails);

    return { name: this.userDetails.name, phone: this.userDetails.phone }
  }

  /**
   *
   * Initiates a chat session using the user's preferred mechanism
   *
   */
  initiate(connectionType, config?: {}) {
    const self = this;
    self.logger.debug('WebexService: initiate: ', connectionType, self.w.webex);

    try {
      // initiate chat with user's selected connection type
      setTimeout(() => {

        // set userdetails data
        let userDetails = {
          userDetails: { ...self.userDetails },
          bypass_trigger: self.userDetails.fields['auto-message'].length > 0 ||
            (self.userDetails.fields['auto-message'].length <= 0 &&
              (
                self.userDetails.fields['gate-number'] == HELP_OPTION_CODES.BAGGAGE
              )
            ),
          lang: self.userDetails.fields.language,
          visitorQueueHash: self.visitor_token
        };

        userDetails.userDetails.fields = this.getCustomReason(userDetails.userDetails.fields)

        // set up conversation end event
        self.w._uww.on(ChatEvents.CONVERSATION_END, (data) => {
          self.logger.debug('WebexService.initialize', ChatEvents.CONVERSATION_END, data);
          self.chatEndEvent.next(data);
          self.conversationHasEnded = true;
          self.analyticsService.sendEvent({
            eventId: QuantumMetricEvents.CONVERSATION_ENDED,
            isConversion: 0,
            value: ''
          })

          this.webexServiceStreamer.next({ action: 'feedback.open', data: {} })
        });

        // set up conversation start event
        self.w._uww.on(ChatEvents.CONVERSATION_START, (data) => {
          self.logger.debug('WebexService.initialize', ChatEvents.CONVERSATION_START, data);
          self.chatStartEvent.next(data);
          self.analyticsService.sendEvent({
            eventId: QuantumMetricEvents.CONVERSION_STARTED,
            isConversion: 1,
            value: ''
          })
        });

        // set up agent joined chat event
        self.w._uww.on(ChatEvents.AGENT_JOINED_CHAT, (data) => {
          self.logger.debug('WebexService.initialize', ChatEvents.AGENT_JOINED_CHAT, data);
          self.agentJoinedEvent.next(data);
          self.analyticsService.sendEvent({
            eventId: QuantumMetricEvents.AGENT_JOINED_CHAT,
            isConversion: 0,
            value: ''
          })
        });

        //session error
        self.w._uww.on("session error", (data) => console.log("xx: on 'session error'", data));

        window.addEventListener("message", event => {
          if (event.origin !== "http://example.org"){
          this.logger.debug('webex message event', event)
          switch (event.data?.type) {
            case "start":
              this.logger.debug('webex started')
              break;
            case "session init":
              break;
            case "no-agents-staffed":
              this.logger.debug('no agent in webex.')
              this.isNoAgents = true;
              this.webexServiceStreamer.next({ action: 'no-agent', data: {} });
              break;
            case "session error":
              this.logger.debug('session error in webex.')
              this.isNoAgents = true;
              this.webexServiceStreamer.next({ action: 'session error', data: {} });
              break;
            case "conversation-end":
              this.logger.debug('conversation ended')
              break;
            case "system-message":
              if (event.data?.message.type === 'join-meeting') {
                this.logger.debug('Webex Meeting ID generated: ', event.data?.message.meetingId);
              }
              if (event.data?.message.type === 'agent-joined') {
                this.logger.debug('agent joined');
                this.webexServiceStreamer.next({ action: 'agent-joined', data: {} });
              }
              if (event.data?.message.type === 'conversation-start') {
                this.logger.debug('conversation started')
                this.webexServiceStreamer.next({ action: 'conversation-start', data: {} });
              }
              break;
            case "q2q-transfer":
              this.callId = event.data?.CallId;
              this.storage.set('callId', this.callId);
              this.webexServiceStreamer.next({ action: 'q2q-transfer', data: {} });
              break;
            default:
              break;
          }
          }
        })

        // launch chat
        let mediaInitalState = "chat"
        if (connectionType === CONNECTION_TYPES.AUDIO.toString()) mediaInitalState = "chat+audio"
        if (connectionType === CONNECTION_TYPES.VIDEO.toString()) mediaInitalState = "chat+audio+video"

        const uwfQueueName = this.departmentRoutingService.departmentRoutingDetails?.routedDepartmentResponse?.response?.uwf_queue_Name;
        let targetSkill = uwfQueueName ? uwfQueueName : 'AOD_General';

        if (
          self.userDetails.fields['airport-code']?.toLowerCase() === 'test' &&
          self.userDetails.fields['gate-number']?.toLowerCase() === 'cisco'
        ) {
          targetSkill = 'AOD_TEST';
        }
        if (
          self.userDetails.fields['airport-code']?.toLowerCase() === 'test' &&
          self.userDetails.fields['gate-number']?.toLowerCase() === 'thousandeyes'
        ) {
          targetSkill = 'AOD_TEMONITOR';
        }
        if (
          self.userDetails.fields['airport-code']?.toLowerCase() === 'test' &&
          self.userDetails.fields['gate-number']?.toLowerCase() === 'appd'
        ) {
          targetSkill = 'AOD_APPDMONITOR';
        }

        const uAgt = navigator.userAgent;
        const osVersion = /OS (\d+)_(\d+)_?(\d+)?/.exec(uAgt);

        const chatConfig = config || {
          targetSkill: targetSkill,
          themeId: 2,
          taskData: {
            UalAgentOnDemand: 'yes',
            ProfileName:'Default',
            TransferEligible: self.userDetails.fields.transferEligible,
            CustomerId: self.userDetails.fields.pnr_no,
            currentLocation: self.userDetails.fields.currentLocation,
            ContactName: self.userDetails.name,
            PhoneNumber: self.userDetails.phone,
            ...self.userDetails.fields
          },
          transferEligible: self.userDetails.fields.transferEligible,
          chatOnly: self.userDetails.fields.chatOnly,
          passengerInfo: '',
          title: 'Support team',
          icon: 'https://www.united.com/favicon.ico',
          languageCode: self.userDetails.fields.language,
          mediaInitalState: mediaInitalState,
          setContactIdAsSessionId: true
        }
        this.storage.set('CHAT_CONFIG', chatConfig);
        self.w._uww.start(chatConfig)

        self.webexReady.next(true)

      }, 50);
    } catch (error) {
      self.logger.error(error);
    }
  }

  getCustomReason(fields: CustomFields) {

    if (fields.referrer === 'FlightOptionsSearchForm') {
      if (fields.reason === HELP_OPTION_CODES.FLIGHT_REBOOK) fields.reason = 'Flight-rebook-SS-datepage'
      if (fields.reason === HELP_OPTION_CODES.FLIGHT_CHANGE) fields.reason = 'Flight-change-SS-datepage'
    }

    if (fields.referrer === 'FlightOptions') {
      if (fields.reason === HELP_OPTION_CODES.FLIGHT_REBOOK) fields.reason = 'Flight-rebook-SS-optionspage'
      if (fields.reason === HELP_OPTION_CODES.FLIGHT_CHANGE) fields.reason = 'Flight-change-SS-optionspage'
    }

    if (fields.referrer === 'FlightOptionsConfirmation') {
      if (fields.reason === HELP_OPTION_CODES.FLIGHT_REBOOK) fields.reason = 'Flight-rebook-SS-confirmationpage'
      if (fields.reason === HELP_OPTION_CODES.FLIGHT_CHANGE) fields.reason = 'Flight-change-SS-confirmationpage'
    }

    return fields
  }

  /**
   * Send custom message from routing API to Webex.
   * UAVA-1361 - comment from Webex team to check condition 'this.w.webexIO.threadManager().chats().length > 0'.
   */
  sendAutoMessage() {
    let autoMessage = this.userDetails.fields['auto-message'].toString();
    if (autoMessage.length > 0) {
      if (this.w.webexIO.threadManager().chats().length > 0) {
        this.w.webexIO.addCustomAutoMessage(autoMessage);
        this.userDetails.fields['auto-message'] = '';
        return;
      }

      setTimeout(() => this.sendAutoMessage(), 200);
    }
  }

  /**
   *
   * Checks whether agents are available
   * @returns Promise - boolean indicating whether agents are available
   */
  async areAgentsAvailable() {
    const promise = new Promise((resolve, reject) => {
      this.logger.debug('WebexService: areAgentsAvailable?');
      // check if we are within working hours
      this.agents.withinWorkingHours = this.isWithinOperatingHours(this.w.webexIO.account().settings);
      this.logger.debug('WebexService: areAgentsAvailable: isWorkingHours: ', this.w.webexIO.account().settings, this.agents.withinWorkingHours);

      // check if any agents are available
      this.agents.available = (webexIO?.onlineAgents()?.length > 0);

      this.logger.debug('WebexService: areAgentsAvailable: agentsAvailable: ', this.w.webexIO.onlineAgents(), this.agents.available);

      // set the error message if no agents
      if (!this.agents.withinWorkingHours) {
        this.agents.noAgentsError = ERROR_KEYS.NON_WORKING_HOURS;
      }
      // set the simple bool
      if (!this.agents.available || !this.agents.withinWorkingHours) {
        this.agents.noAgents = true;
        resolve(false);
      } else {
        resolve(true);
      }
    });
    return promise;
  }

  /**
   *
   * Check operating hours
   *
   * @param settings - The webex widget settings object
   *
   * @returns - true or false based on comparison of current time to operating hours
   *
   */
  isWithinOperatingHours(settings) {
    if (settings) {

      if (settings.working_hours_toggle === '0') {
        this.logger.debug('Webex Service: operatingHours: working_hours_toggle off, stopping check: ', settings.working_hours_toggle);
        return true;
      }

      this.logger.debug('typeof toggle: ', typeof settings.working_hours_toggle);

      if (settings.timezone && settings.working_schedule) {
        this.logger.debug('Webex Service: operatingHours: timezone:', settings.timezone);
        this.logger.debug('Webex Service: operatingHours: working_schedule:', settings.working_schedule);

        // get timing data
        const wkSchedule = JSON.parse(settings.working_schedule);
        const nowAdjusted = Temporal.Now.instant().toZonedDateTimeISO(settings.timezone);
        const nowHourMinute = String(nowAdjusted.hour).padStart(2, '0') +
          ':' + String(nowAdjusted.minute).padStart(2, '0');
        this.logger.debug('Webex Service: operatingHours: Time in Chicago:', nowAdjusted.toString(), ' ', nowHourMinute);

        // return check result
        const isDuringSomeWorkingHours = wkSchedule.some((obj) => {
          const isDuringOperatingHours = (obj.working_day === WeekDay[(nowAdjusted.dayOfWeek) - 1]
            && nowHourMinute >= obj.working_from_time
            && nowHourMinute <= obj.working_to_time);
          return isDuringOperatingHours;
        });
        this.logger.debug('Webex Service: operatingHours: Within Operating Hours Check:', isDuringSomeWorkingHours);
        return isDuringSomeWorkingHours;
      }
    } else {
      this.logger.error('Webex Service: operatingHours: Webex settings object not available, operating hours check failed, returning true to be safe');
      return true;
    }
  }

  /**
   * Reload window object
   */
  reloadWindow() {
    window.location.reload();
  }

  initCallCenter(config?: {}) {
    const uwfQueueName = this.departmentRoutingService.departmentRoutingDetails?.routedDepartmentResponse?.response?.uwf_queue_Name;
    let targetSkill = uwfQueueName ? uwfQueueName : 'AOD_General';
    window.addEventListener("message",event =>{
      if (event.origin !== "http://example.org"){
      switch(event.data?.message?.type){
        case "call-ended":
          this.webexServiceStreamer.next({ action: 'survey.open', data: {} })
          break;
      }
    }
    })

    if (
      this.userDetails.fields['airport-code']?.toLowerCase() === 'test' &&
      this.userDetails.fields['gate-number']?.toLowerCase() === 'cisco'
    ) {
      targetSkill = 'AOD_TEST';
    }
    if (
      this.userDetails.fields['airport-code']?.toLowerCase() === 'test' &&
      this.userDetails.fields['gate-number']?.toLowerCase() === 'thousandeyes'
    ) {
      targetSkill = 'AOD_TEMONITOR';
    }
    if (
      this.userDetails.fields['airport-code']?.toLowerCase() === 'test' &&
      this.userDetails.fields['gate-number']?.toLowerCase() === 'appd'
    ) {
      targetSkill = 'AOD_APPDMONITOR';
    }
   
    (function (host, ts, e) {
      const obj = "_uwwc"
      const win = window
      win[obj] = {
        queue: [],
        start: (args) => win[obj].queue.push(args),
        customerService: host
      }
      e = win.document.createElement("script")
      e.defer = 1
      e.src = `${host}/Q2QFrontEnd/widget/widget.js`
      e.type = 'module'
      document.body.appendChild(e)
    })(ENVIRONMENT.PROPERTIES.EXTERNAL_API.CALL_CENTER, Math.round(Date.now() / (60 * 1000)));
    if (this.callId == "" && this.userDetails.fields.callId != "0") {
      this.callId = this.userDetails.fields.callId;
      this.storage.set('callId', this.callId);
    }

    const callCenterConfig = config || {
      targetSkill: targetSkill,
      callId: this.callId ? this.callId : null,
      transferType: 'PreRoute',
      preRouteReason: null,
      sessionId: null,
      autoStart: true,
      themeId: 2,
      chatParams: {
        UalAgentOnDemand: 'yes',
        CustomerId: this.userDetails.fields.pnr_no,
        currentLocation: this.userDetails.fields.currentLocation,
        ContactName: this.userDetails.name,
        PhoneNumber: this.userDetails.phone,
        ...this.userDetails.fields
      },
      title: 'Support',
      languageCode: this.userDetails.fields.language,
      setContactIdAsSessionId: true
    };
    this.storage.set('CALL_CENTER_CONFIG', callCenterConfig);
    window['_uwwc'].start(callCenterConfig)
  }

  async getSkillStatus() {
    let mediaInitalState = "chat"
    if (this.connectionType === CONNECTION_TYPES.AUDIO.toString()) mediaInitalState = "chat+audio"
    if (this.connectionType === CONNECTION_TYPES.VIDEO.toString()) mediaInitalState = "chat+audio+video"
    const uwfQueueName = this.departmentRoutingService.departmentRoutingDetails?.routedDepartmentResponse?.response?.uwf_queue_Name;
    const attr = {
      UalAgentOnDemand: 'yes',
      CustomerId: this.userDetails.fields.pnr_no,
      currentLocation: this.userDetails.fields.currentLocation,
      ContactName: this.userDetails.name,
      PhoneNumber: this.userDetails.phone,
      ...this.userDetails.fields,
    }
    const body = {
      UALAODSkillName: uwfQueueName ? uwfQueueName : 'AOD_General_Only',
      MediaType: mediaInitalState,
      ContactName: this.userDetails.name,
      AODAttributes: JSON.stringify(attr)
    }
    const headers = {
      Authorization: 'Basic UTJROlBAc3N3b3JkIzE=',
      'Content-Type': 'application/json'
    }
    const response = await new Promise((res) => {
      this.http.post(ENVIRONMENT.PROPERTIES.EXTERNAL_API.GET_SKILL_STATUS, JSON.stringify(body), { headers }).pipe(
        retry({
          count: ENVIRONMENT.PROPERTIES.UW_CONFIG.HEALTH_CHECK_API.RETRY.COUNT,
          delay: ENVIRONMENT.PROPERTIES.UW_CONFIG.HEALTH_CHECK_API.RETRY.DELAY
        }),
        timeout(ENVIRONMENT.PROPERTIES.UW_CONFIG.HEALTH_CHECK_API.TIMEOUT),
        catchError(e => {
          return of({ success: false })
        }),
      ).subscribe(data => {
        res(data);
      })
    })


    return response
  }

  async checkSkillStatus() {
    const uwfQueueName = this.departmentRoutingService.departmentRoutingDetails?.routedDepartmentResponse?.response?.uwf_queue_Name;
    let targetSkill = uwfQueueName ? uwfQueueName : 'AOD_General_Only';

    if (
      this.userDetails.fields['airport-code']?.toLowerCase() === 'test' &&
      this.userDetails.fields['gate-number']?.toLowerCase() === 'cisco'
    ) {
      targetSkill = 'AOD_TEST';
    }
    if (
      this.userDetails.fields['airport-code']?.toLowerCase() === 'test' &&
      this.userDetails.fields['gate-number']?.toLowerCase() === 'thousandeyes'
    ) {
      targetSkill = 'AOD_TEMONITOR';
    }
    if (
      this.userDetails.fields['airport-code']?.toLowerCase() === 'test' &&
      this.userDetails.fields['gate-number']?.toLowerCase() === 'appd'
    ) {
      targetSkill = 'AOD_APPDMONITOR';
    }

    const headers = {
      Authorization: 'Basic dHBzdmM6OEE0RmMjWG5xcmYh',
      'Content-Type': 'application/json',
      'X-UWF-FinesseHost': 'united.upstreamworkssoftware.com'
    }
    const body = {
      skillAddress: [targetSkill]
    }
    ENVIRONMENT.PROPERTIES.UW_CONFIG.HEALTH_CHECK_API.RETRY.COUNT
    const newapi = "https://custservice-lab.united.upstreamworkssoftware.com/api/floatingchat/checkskillstatus?addresses="
    const response = await new Promise((res, rej) => {
      this.http.get(ENVIRONMENT.PROPERTIES.EXTERNAL_API.CHECK_SKILL_STATUS + targetSkill).pipe(
        retry({
          count: ENVIRONMENT.PROPERTIES.UW_CONFIG.HEALTH_CHECK_API.RETRY.COUNT,
          delay: ENVIRONMENT.PROPERTIES.UW_CONFIG.HEALTH_CHECK_API.RETRY.DELAY
        }),
        timeout(ENVIRONMENT.PROPERTIES.UW_CONFIG.HEALTH_CHECK_API.TIMEOUT),
        catchError(err => {
          return of({ success: false })
        })
      ).subscribe(data => {
        res(data);
      })
    })
    return response
  }
  
 async checkBandwidth() {
    const uwfQueueName = this.departmentRoutingService.departmentRoutingDetails?.routedDepartmentResponse?.response?.uwf_queue_Name;
    const body = blob;
    const headers = {
      Authorization: ENVIRONMENT.PROPERTIES.EXTERNAL_API.BANDWIDTH_AUTH,
      'Content-Type': 'application/json'
    }
    const response = await new Promise((res, rej) => {
      this.http.post(ENVIRONMENT.PROPERTIES.EXTERNAL_API.BANDWIDTH_CHECK, body, { headers }).pipe(
        retry({
          count: ENVIRONMENT.PROPERTIES.UW_CONFIG.HEALTH_CHECK_API.RETRY.COUNT,
          delay: ENVIRONMENT.PROPERTIES.UW_CONFIG.HEALTH_CHECK_API.RETRY.DELAY
        }),
        timeout(ENVIRONMENT.PROPERTIES.UW_CONFIG.HEALTH_CHECK_API.TIMEOUT),
        catchError(err => {
          return of({ success: false })
        })
      ).subscribe(data => {
        res(data);
      })
    })
    return response
 }
}
